import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PageHeader from '../components/PageHeader';
import { AlertTriangle, Camera, ClipboardCheck, Clock, MapPin, MapPinOff, Paperclip, X, CalendarDays } from 'lucide-react';
import ModuleAccessDenied from '../components/ui/ModuleAccessDenied';
import timesheetService, { type TimesheetStatus, type TimesheetSummary, type DailyCategoryRow } from '../services/timesheet';
import timesheetApprovalService, { type TimesheetApprovalRequest, type ScheduledDay } from '../services/timesheetApproval';
import leaveService, { type LeaveRequest, type LeaveType, type LeaveBalance, type ProjectedBalancePreview, type LeaveDayInfo, type CreateLeaveRequest, negativeBalanceRefusal, canGoNegative, MAX_NEGATIVE_LEAVE_HOURS } from '../services/leave';
import workScheduleService, { type LeaveHoursPreview } from '../services/workScheduleService';
import type { SickCertCheck } from '../services/leave';
import { useOrgTimezone, formatTimeInTz, formatDateInTz, formatLocalDateInTz, getTodayInTz } from '../utils/timezone';
import { formatDuration } from '../utils/time';
import { useLocation } from '../hooks/useLocation';
import { useAuth } from '../context/AuthContext';
import PolicySummaryCard from '../components/PolicySummaryCard';
import timeInLieuService, { type MyTilStatus } from '../services/timeInLieu';
import { toNum, toNumOr } from '@/utils/numeric';

// Minutes from midnight for an 'HH:mm' (or 'HH:mm:ss') time-of-day string.
const timeToMinutes = (t: string): number => {
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

function categoryStyle(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('overtime') || c.includes('1.5')) return 'bg-orange-100 text-orange-700';
  if (c.includes('time in lieu') || c.includes('til')) return 'bg-indigo-100 text-indigo-700';
  if (c.includes('public holiday')) return 'bg-purple-100 text-purple-700';
  // Personal/sick leave maps to the 'Personal Leave' payroll category.
  if (c.includes('personal')) return 'bg-red-100 text-red-700';
  if (c.includes('annual') || c.includes('leave')) return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
}

function CategoryBadge({ category, hours }: { category: string; hours: number }) {
  const isStandard = category.toLowerCase() === 'standard';
  if (isStandard) return null;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${categoryStyle(category)}`}>
      {category} {formatDuration(hours)}
    </span>
  );
}

const BALANCE_TRACK_TYPES: LeaveType[] = ['annual', 'personal', 'time_in_lieu'];

const Timesheet: React.FC = () => {
  const { t } = useTranslation('timesheet', { keyPrefix: 'Timesheet' });
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgTimezone = useOrgTimezone();
  const { location: currentLocation, error: locationError, loading: locationLoading, requestLocation } = useLocation();
  const isFortnightly = user?.pay_frequency === 'fortnightly';
const [status, setStatus] = useState<TimesheetStatus | null>(null);
  const [summary, setSummary] = useState<TimesheetSummary | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once a clock attempt has failed and another is on the way. Clock calls
  // ride out a weak link for up to 45 s, and an unexplained spinner that long
  // gets pressed again or walked away from.
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipLunchDeduction, setSkipLunchDeduction] = useState(false);
  // Working through lunch is only for someone whose time in lieu agreement is
  // in force (2026-10-08). Ticking the box without one explains why instead,
  // and offers the agreement.
  const [til, setTil] = useState<MyTilStatus | null>(null);
  const [lunchNeedsTil, setLunchNeedsTil] = useState(false);
  const [lunchNotice, setLunchNotice] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    timeInLieuService.getMine()
      .then(res => { if (!cancelled && res.success && res.data) setTil(res.data); })
      .catch(() => { /* reads as no agreement, which is the default */ });
    return () => { cancelled = true; };
  }, []);
  const tilInForce = til?.choice === 'time_in_lieu';
  const [moduleBlocked, setModuleBlocked] = useState(false);

  // Timesheet approval state
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submissionStartDate, setSubmissionStartDate] = useState('');
  const [submissionEndDate, setSubmissionEndDate] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<string | null>(null);
  const [timesheetSubmissions, setTimesheetSubmissions] = useState<TimesheetApprovalRequest[]>([]);
  const [submissionPreview, setSubmissionPreview] = useState<{ scheduled_working_days: number; scheduled_hours: number; scheduled_days: ScheduledDay[] } | null>(null);
  const [submissionPreviewLoading, setSubmissionPreviewLoading] = useState(false);
  const [scheduledByPeriod, setScheduledByPeriod] = useState<Map<string, ScheduledDay[]>>(new Map());
  const [prevSummary, setPrevSummary] = useState<TimesheetSummary | null>(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState<Map<string, DailyCategoryRow[]>>(new Map());
  const [quickSubmittingPeriod, setQuickSubmittingPeriod] = useState<string | null>(null);

  // Leave management state
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveType, setLeaveType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [leaveSuccess, setLeaveSuccess] = useState<string | null>(null);
  const [isHourlyLeave, setIsHourlyLeave] = useState(false);
  // Partial-day leave is expressed as an explicit time-of-day window rather
  // than a raw hours number. `leaveHours` is the bookable duration for that
  // window, used for previews.
  const [leaveStartTime, setLeaveStartTime] = useState('');
  const [leaveEndTime, setLeaveEndTime]   = useState('');
  // Schedule + worked-hours summary for the picked date, used by the
  // partial-day leave helpers ("Leave for rest of scheduled day", auto-fill
  // end_time from the user's scheduled finish).
  const [leaveDayInfo, setLeaveDayInfo] = useState<LeaveDayInfo | null>(null);
  // Bookable hours for the picked window. Mirrors the server's derivation in
  // routes/leave.js: the window's wall-clock span scaled so that a day's gaps
  // add up to exactly that day's shortfall. The span and the paid hours differ
  // whenever the unpaid break falls inside the window — showing the raw span
  // here would promise more leave than actually gets booked.
  const leaveHours = (() => {
    if (!leaveStartTime || !leaveEndTime) return '';
    const mins = timeToMinutes(leaveEndTime) - timeToMinutes(leaveStartTime);
    if (!Number.isFinite(mins) || mins <= 0) return '';
    const info = leaveDayInfo;
    if (!info || !info.is_working_day) return (mins / 60).toFixed(2);
    const gapMinutes = info.uncovered_segments.reduce(
      (n, g) => n + (timeToMinutes(g.end_time) - timeToMinutes(g.start_time)), 0
    );
    const ratio = gapMinutes > 0
      ? Math.min(1, (info.remaining_scheduled_hours * 60) / gapMinutes)
      : 1;
    return ((mins * ratio) / 60).toFixed(2);
  })();
  const [leaveHoursPreview, setLeaveHoursPreview] = useState<LeaveHoursPreview | null>(null);
  const [leaveHoursPreviewLoading, setLeaveHoursPreviewLoading] = useState(false);
  const [balancePreview, setBalancePreview] = useState<ProjectedBalancePreview | null>(null);
  const [balancePreviewLoading, setBalancePreviewLoading] = useState(false);
  const [tilBalance, setTilBalance] = useState<number | null>(null);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [activeTab, setActiveTab] = useState<'timesheet' | 'leave'>('timesheet');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [certCheck, setCertCheck] = useState<SickCertCheck | null>(null);
  const certFileInputRef = useRef<HTMLInputElement>(null);
  const certCameraInputRef = useRef<HTMLInputElement>(null);

  // Pull-to-refresh: re-run the on-mount fetch effect
  const [refreshTick, setRefreshTick] = useState(0);
  useAppRefresh(useCallback(() => setRefreshTick(n => n + 1), []));

  // Re-fetch all data on mount and whenever the active organisation changes.
  useEffect(() => {
    fetchStatus();
    fetchSummary();
    fetchTimesheetSubmissions();
    fetchLeaveRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisation_id, refreshTick]);

  const isSickLeave = leaveType === 'personal';
  const certRequired = !!(isSickLeave && certCheck && certCheck.required);
  const certBlocksApproval = certRequired && certCheck?.enforcement === 'block_approval';
  const certWarnOnly = certRequired && certCheck?.enforcement !== 'block_approval';

  useEffect(() => {
    if (certBlocksApproval) setHasCertificate(true);
  }, [certBlocksApproval]);

  // Personal leave: ask the server whether a doctor's certificate is required
  // for the chosen date range. The server evaluates every active rule
  // (always-required, after-N-days with split detection, adjacent to
  // weekend/PH/other leave) so the UI matches what the approval gate will do.
  useEffect(() => {
    if (!isSickLeave || !startDate || !endDate || endDate < startDate) {
      setCertCheck(null);
      return;
    }
    let cancelled = false;
    leaveService.checkSickCert(startDate, endDate)
      .then(res => {
        if (cancelled) return;
        if (res.success) setCertCheck(res.data);
      })
      .catch(() => { if (!cancelled) setCertCheck(null); });
    return () => { cancelled = true; };
  }, [isSickLeave, startDate, endDate]);

  // Re-fetch summary if pay_frequency loads after initial mount
  useEffect(() => {
    if (user?.pay_frequency !== undefined) {
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.pay_frequency]);

  // Fetch scheduled working-day / scheduled-hours preview whenever the user
  // changes the dates on the submission form. Debounced via the React state
  // changes themselves — no spam thanks to the date-input UX.
  useEffect(() => {
    if (!showSubmitForm || !submissionStartDate || !submissionEndDate) {
      setSubmissionPreview(null);
      return;
    }
    if (submissionEndDate < submissionStartDate) {
      setSubmissionPreview(null);
      return;
    }
    let cancelled = false;
    setSubmissionPreviewLoading(true);
    timesheetApprovalService.getPeriodPreview(submissionStartDate, submissionEndDate)
      .then(res => {
        if (cancelled) return;
        if (res.success) {
          setSubmissionPreview({
            scheduled_working_days: res.data.scheduled_working_days,
            scheduled_hours: res.data.scheduled_hours,
            scheduled_days: res.data.scheduled_days || []
          });
        }
      })
      .catch(() => { if (!cancelled) setSubmissionPreview(null); })
      .finally(() => { if (!cancelled) setSubmissionPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [showSubmitForm, submissionStartDate, submissionEndDate]);

  // Fetch leave balances on mount and when org changes
  useEffect(() => {
    leaveService.getBalances()
      .then(r => { if (r.success) setLeaveBalances(r.data); })
      .catch(() => {});
  }, [user?.organisation_id]);

  // Fetch TIL balance when leave tab opens (fortnightly employees only)
  useEffect(() => {
    if (activeTab !== 'leave' || !isFortnightly) return;
    leaveService.getTilBalance()
      .then(r => { if (r.success) setTilBalance(toNum(r.data.available_hours)); })
      .catch(() => {});
  }, [activeTab, isFortnightly]);

  // Fetch scheduled-hours preview when leave dates change (full-day leave only)
  useEffect(() => {
    if (isHourlyLeave || !startDate || !endDate) {
      setLeaveHoursPreview(null);
      return;
    }
    let cancelled = false;
    setLeaveHoursPreviewLoading(true);
    workScheduleService.previewLeaveHours(startDate, endDate)
      .then(response => {
        if (!cancelled && response.success) setLeaveHoursPreview(response.data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLeaveHoursPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, isHourlyLeave]);

  // Fetch the day's schedule + worked-hours summary when partial-day leave is
  // active. Powers the "Leave for rest of scheduled day" helper button and
  // the auto-fill of end_time from the user's scheduled finish.
  useEffect(() => {
    if (!isHourlyLeave || !startDate) {
      setLeaveDayInfo(null);
      return;
    }
    let cancelled = false;
    leaveService.getDayInfo(startDate)
      .then(r => { if (!cancelled && r.success) setLeaveDayInfo(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [startDate, isHourlyLeave]);

  // Fetch projected balance when leave type and start date change
  useEffect(() => {
    if (!startDate || !BALANCE_TRACK_TYPES.includes(leaveType)) {
      setBalancePreview(null);
      return;
    }
    let cancelled = false;
    setBalancePreviewLoading(true);
    leaveService.previewProjectedBalance(leaveType, startDate)
      .then(response => {
        if (!cancelled && response.success) setBalancePreview(response.data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setBalancePreviewLoading(false); });
    return () => { cancelled = true; };
  }, [leaveType, startDate]);

  const fetchStatus = async () => {
    try {
      const response = await timesheetService.getStatus();
      if (response.success) {
        setStatus(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch status:', error);
      if (error.status === 403) {
        setModuleBlocked(true);
      } else {
        setError(t('errors.fetchStatus'));
      }
    }
  };

  const fetchSummary = async () => {
    try {
      const period = isFortnightly ? 'fortnight' : 'week';
      const response = await timesheetService.getSummary({ period });

      if (response.success) {
        const current = response.data.summary;
        setSummary(current);

        const newBreakdown = new Map<string, DailyCategoryRow[]>();
        if (response.data.dailyCategoryBreakdown?.length) {
          newBreakdown.set(current.period_start, response.data.dailyCategoryBreakdown);
        }

        // Fetch previous period summary.
        // Use pure UTC arithmetic on the YYYY-MM-DD string to avoid local-midnight
        // drift: new Date("YYYY-MM-DD") is UTC midnight; setDate() operates in local
        // time and produces local midnight, so toISOString() returns the previous UTC
        // date in UTC+ timezones (e.g. AEDT UTC+11 → off by 1 day).
        // Derive the previous period's length from the actual span the backend
        // returned for the current period, not from auth-context pay_frequency.
        // The auth context can lag (initial load) or disagree with the user's
        // DB pay_frequency, which would produce a 7-day "prev" for what should
        // be a fortnightly user — exactly what the prior bug was doing.
        if (current.period_start && current.period_end) {
          const [csYr, csMo, csDa] = current.period_start.substring(0, 10).split('-').map(Number);
          const [ceYr, ceMo, ceDa] = current.period_end.substring(0, 10).split('-').map(Number);
          const startMs = Date.UTC(csYr, csMo - 1, csDa);
          const endMs = Date.UTC(ceYr, ceMo - 1, ceDa);
          const periodDays = Math.round((endMs - startMs) / 86_400_000) + 1;
          const [yr, mo, da] = current.period_start.substring(0, 10).split('-').map(Number);
          const prevEndDate = new Date(Date.UTC(yr, mo - 1, da - 1));
          const prevStartDate = new Date(Date.UTC(yr, mo - 1, da - periodDays));
          const fmt = (d: Date) => d.toISOString().substring(0, 10);
          const prevRes = await timesheetService.getSummary({ startDate: fmt(prevStartDate), endDate: fmt(prevEndDate) });
          if (prevRes.success && toNumOr(prevRes.data.summary.total_hours) > 0) {
            setPrevSummary(prevRes.data.summary);
            if (prevRes.data.dailyCategoryBreakdown?.length) {
              newBreakdown.set(prevRes.data.summary.period_start, prevRes.data.dailyCategoryBreakdown);
            }
          } else {
            setPrevSummary(null);
          }
        }

        setCategoryBreakdown(newBreakdown);
      } else {
        console.error('Summary fetch failed:', response);
      }
    } catch (error: any) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const response = await leaveService.getRequests();
      if (response.success) {
        // Filter to current org only as a client-side safeguard against cross-org leakage.
        const orgId = user?.organisation_id;
        setLeaveRequests(orgId ? response.data.filter(r => r.organisation_id === orgId) : response.data);
      }
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    }
  };

  const fetchTimesheetSubmissions = async () => {
    try {
      const response = await timesheetApprovalService.getSubmissions();
      if (response.success) {
        setTimesheetSubmissions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch timesheet submissions:', error);
    }
  };

  const handleClockIn = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Attempt to get location; proceed even if unavailable
      const loc = await requestLocation();
      const response = await timesheetService.clockIn({
        notes: notes || undefined,
        latitude: loc?.coords.latitude,
        longitude: loc?.coords.longitude,
        accuracy: loc?.coords.accuracy,
      }, { onRetry: () => setRetrying(true) });
      if (response.success) {
        setNotes('');
        await fetchStatus();
        await fetchSummary();

        // If the user has paused or pending farm jobs / sprays assigned to
        // them, land them on the "Resume your work" screen with that data.
        // Failures here must not surface to the user — they're already
        // clocked in, the redirect is a quality-of-life convenience.
        try {
          const work = await timesheetService.getPostClockinWork();
          if (work.success) {
            const d = work.data;
            const total =
              d.auto_paused_jobs.length +
              d.manual_paused_jobs.length +
              d.pending_jobs.length +
              d.auto_paused_sprays.length +
              d.manual_paused_sprays.length +
              d.pending_sprays.length;
            if (total > 0) {
              navigate('/work/resume', { state: d });
            }
          }
        } catch (workErr) {
          // Swallow — non-critical
          console.error('post-clockin-work fetch failed', workErr);
        }
      }
    } catch (error: any) {
      setError(error.message || t('errors.clockIn'));
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const handleClockOut = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Attempt to get location; proceed even if unavailable
      const loc = await requestLocation();
      const response = await timesheetService.clockOut({
        notes: notes || undefined,
        latitude: loc?.coords.latitude,
        longitude: loc?.coords.longitude,
        accuracy: loc?.coords.accuracy,
        skip_lunch_deduction: skipLunchDeduction || undefined,
      }, { onRetry: () => setRetrying(true) });
      if (response.success) {
        setNotes('');
        setSkipLunchDeduction(false);
        setLunchNeedsTil(false);
        // The server deducted the lunch after all (no agreement on the day).
        setLunchNotice((response as { warning?: string }).warning || null);
        await fetchStatus();
        await fetchSummary();
      }
    } catch (error: any) {
      setError(error.message || t('errors.clockOut'));
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const handleLeaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      setLeaveError(t('errors.selectDates'));
      return;
    }

    if (isHourlyLeave && (!leaveStartTime || !leaveEndTime || !leaveHours)) {
      setLeaveError(t('errors.pickLeaveTimes'));
      return;
    }

    if (certBlocksApproval && !certificateFile) {
      const reasons = certCheck?.reasons.join(' ') || t('errors.certPolicy');
      setLeaveError(t('errors.certAttach', { reasons }));
      return;
    }

    setLeaveLoading(true);
    setLeaveError(null);
    setLeaveSuccess(null);

    try {
      // Send plain YYYY-MM-DD strings — the backend expects date strings for DATE columns.
      const payload: CreateLeaveRequest = {
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate,
        reason: leaveReason || undefined,
        // `hours` is deliberately omitted for partial-day leave: the server
        // derives it from the window against the day's schedule and worked
        // hours, and ignores any client-supplied value.
        start_time: isHourlyLeave ? leaveStartTime : undefined,
        end_time:   isHourlyLeave ? leaveEndTime   : undefined
      };
      let response;
      try {
        response = await leaveService.createRequest(payload);
      } catch (err) {
        // More than the balance: say how far below zero it goes, and send it
        // again if the employee still wants it. Their supervisor decides.
        const negative = negativeBalanceRefusal(err);
        if (!negative || !canGoNegative(negative)) throw err;
        if (!window.confirm(t('requestAnyway', { message: negative.message }))) {
          setLeaveError(negative.message);
          return;
        }
        response = await leaveService.createRequest({ ...payload, allow_negative_balance: true });
      }

      if (response.success) {
        if (certificateFile && response.data?.id) {
          try {
            await leaveService.uploadCertificate(response.data.id, certificateFile);
          } catch {
            setLeaveSuccess(t('leaveSubmittedCertFailed'));
            setShowLeaveForm(false);
            resetLeaveForm();
            await fetchLeaveRequests();
            leaveService.getBalances().then(r => { if (r.success) setLeaveBalances(r.data); }).catch(() => {});
            return;
          }
        }
        // The server trims the window around hours already worked — if it did,
        // say so rather than letting the times change silently.
        const adjustNote = (response as { warning?: string }).warning;
        setLeaveSuccess(adjustNote
          ? t('leaveSubmittedAdjusted', { note: adjustNote })
          : t('leaveSubmitted'));
        setShowLeaveForm(false);
        resetLeaveForm();
        await fetchLeaveRequests();
        leaveService.getBalances()
          .then(r => { if (r.success) setLeaveBalances(r.data); })
          .catch(() => {});
      }
    } catch (error: any) {
      console.error('Leave request error:', error);
      // Prefer the server-side error message (e.g. TS_OVERLAP details) so the
      // user sees what's actually blocking them, not an axios-wrapped HTTP status.
      const serverMsg = error?.response?.data?.error?.message;
      setLeaveError(serverMsg || error.message || t('errors.submitLeave'));
    } finally {
      setLeaveLoading(false);
    }
  };

  const handleTimesheetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submissionStartDate || !submissionEndDate) {
      setSubmissionError(t('errors.selectDates'));
      return;
    }

    setSubmissionLoading(true);
    setSubmissionError(null);
    setSubmissionSuccess(null);

    try {
      const response = await timesheetApprovalService.submitForApproval({
        start_date: submissionStartDate,
        end_date: submissionEndDate,
        notes: submissionNotes || undefined
      });

      if (response.success) {
        setSubmissionSuccess(t('timesheetSubmitted'));
        setShowSubmitForm(false);
        resetTimesheetForm();
        await fetchTimesheetSubmissions();
      }
    } catch (error: any) {
      console.error('Timesheet submission error:', error);
      setSubmissionError(error.response?.data?.error?.message || error.message || t('errors.submitTimesheet'));
    } finally {
      setSubmissionLoading(false);
    }
  };

  const handleRecallSubmission = async (id: number) => {
    if (!confirm(t('confirmRecall'))) {
      return;
    }
    try {
      const response = await timesheetApprovalService.recallSubmission(id);
      if (response.success) {
        setSubmissionSuccess(t('recalled'));
        await fetchTimesheetSubmissions();
        await fetchSummary();
      }
    } catch (error: any) {
      setSubmissionError(error.response?.data?.error?.message || error.message || t('errors.recall'));
    }
  };

  const handleCancelLeave = async (id: number) => {
    if (!confirm(t('confirmCancelLeave'))) {
      return;
    }

    try {
      const response = await leaveService.cancelRequest(id);
      if (response.success) {
        setLeaveSuccess(t('leaveCancelled'));
        await fetchLeaveRequests();
      }
    } catch (error: any) {
      console.error('Cancel leave request error:', error);
      setLeaveError(error.message || t('errors.cancelLeave'));
    }
  };

  const resetLeaveForm = () => {
    setLeaveType('annual');
    setStartDate('');
    setEndDate('');
    setLeaveReason('');
    setIsHourlyLeave(false);
    setLeaveStartTime('');
    setLeaveEndTime('');
    setCertificateFile(null);
    setHasCertificate(false);
    if (certFileInputRef.current) certFileInputRef.current.value = '';
    if (certCameraInputRef.current) certCameraInputRef.current.value = '';
  };

  const resetTimesheetForm = () => {
    setSubmissionStartDate('');
    setSubmissionEndDate('');
    setSubmissionNotes('');
  };

  const handleQuickSubmit = async (periodStart: string, periodEnd: string) => {
    setQuickSubmittingPeriod(periodStart);
    setSubmissionError(null);
    setSubmissionSuccess(null);

    try {
      const response = await timesheetApprovalService.submitForApproval({
        start_date: periodStart,
        end_date: periodEnd,
      });
      if (response.success) {
        setSubmissionSuccess(t('timesheetSubmitted'));
        await fetchTimesheetSubmissions();
        await fetchSummary();
      }
    } catch (error: any) {
      setSubmissionError(error.response?.data?.error?.message || error.message || t('errors.submitTimesheet'));
    } finally {
      setQuickSubmittingPeriod(null);
    }
  };

  const formatTime = (dateString: string) => {
    return formatTimeInTz(dateString, orgTimezone);
  };

  const getCurrentWorkingTime = () => {
    if (!status?.hasActiveSessions || status.status !== 'clocked_in' || !status.activeSessions || status.activeSessions.length === 0) {
      return null;
    }

    // Always calculate from the actual clock_in_time so that employees who clock
    // in before their scheduled start do not see negative working hours.
    // (Server-provided current_hours may be based on the scheduled start time.)
    const mostRecentSession = status.activeSessions[0];
    const clockInTime = new Date(mostRecentSession.clock_in_time);
    const now = new Date();
    const diffInHours = (now.getTime() - clockInTime.getTime()) / (1000 * 60 * 60);

    return formatDuration(diffInHours);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Heading on a leave request card, e.g. "Annual Leave".
  const leaveRequestTitle = (type: LeaveType): string => {
    switch (type) {
      case 'annual': return t('requestTitle.annual');
      case 'personal': return t('requestTitle.personal');
      case 'unpaid': return t('requestTitle.unpaid');
      case 'other': return t('requestTitle.other');
      case 'time_in_lieu': return t('requestTitle.time_in_lieu');
      case 'long_service': return t('requestTitle.long_service');
      default: {
        const raw = String(type);
        return t('requestTitle.fallback', { type: raw.charAt(0).toUpperCase() + raw.slice(1) });
      }
    }
  };

  // Row label in the Leave Entitlements card.
  const balanceLabel = (type: string): string => {
    switch (type) {
      case 'time_in_lieu': return t('balanceLabel.time_in_lieu');
      case 'personal': return t('balanceLabel.personal');
      case 'annual': return t('balanceLabel.annual');
      case 'long_service': return t('balanceLabel.long_service');
      case 'unpaid': return t('balanceLabel.unpaid');
      case 'other': return t('balanceLabel.other');
      default: return t('balanceLabel.fallback', { type: type.replace('_', ' ') });
    }
  };

  // A leave type option, with the hours available where a balance is kept.
  const withAvailable = (label: string, hours: number | null | undefined) =>
    hours === null || hours === undefined
      ? label
      : t('leaveOption.withAvailable', { label, hours: hours.toFixed(1) });
  const availableFor = (type: LeaveType) => {
    const b = leaveBalances.find(lb => lb.leave_type === type);
    return b ? Number(b.available_hours) : null;
  };

  const isClocked = status?.status === 'clocked_in';
  const currentWorkingTime = getCurrentWorkingTime();
  const sessionHours = status?.activeSessions?.[0]?.current_hours ?? 0;
  const lunchEligible = isClocked && sessionHours > 4;

  // Fetch scheduled-day breakdowns for each unsubmitted period so the card
  // can highlight days the employee was rostered but didn't clock.
  useEffect(() => {
    const periods: { start: string; end: string }[] = [];
    if (prevSummary?.period_start && prevSummary?.period_end) {
      periods.push({ start: prevSummary.period_start, end: prevSummary.period_end });
    }
    if (summary?.period_start && summary?.period_end) {
      periods.push({ start: summary.period_start, end: summary.period_end });
    }
    if (periods.length === 0) return;
    let cancelled = false;
    Promise.all(periods.map(p =>
      timesheetApprovalService.getPeriodPreview(p.start, p.end)
        .then(res => res.success ? { start: p.start, days: res.data.scheduled_days || [] } : null)
        .catch(() => null)
    )).then(results => {
      if (cancelled) return;
      setScheduledByPeriod(prev => {
        const next = new Map(prev);
        for (const r of results) {
          if (r) next.set(r.start, r.days);
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [prevSummary?.period_start, prevSummary?.period_end, summary?.period_start, summary?.period_end]);

  const todayLocal = getTodayInTz(orgTimezone);
  const isUnsubmitted = (s: TimesheetSummary) => {
    if (!(toNumOr(s.total_hours) > 0 && !!s.period_start && !!s.period_end)) {
      return false;
    }
    // Only offer quick-submit once the pay cycle has fully elapsed — otherwise
    // the truncated `effectiveEnd = min(period_end, today)` would submit only a
    // partial period (e.g. 7 of 14 days for a fortnightly user mid-cycle).
    if (s.period_end > todayLocal) {
      return false;
    }
    // Use the server-authoritative flag when present (entry updated_at vs
    // submission submitted_at — same logic as the submit validation endpoint).
    return s.has_unsubmitted_entries ?? false;
  };

  const unsubmittedPeriods = [prevSummary, summary]
    .filter((s): s is TimesheetSummary => s !== null && isUnsubmitted(s));

  if (moduleBlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          icon={<Clock className="h-6 w-6 text-white" />}
          showHistoryButton={false}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ModuleAccessDenied moduleName={t('moduleName')} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Clock className="h-6 w-6 text-white" />}
        showHistoryButton={activeTab === 'timesheet'}
        onHistoryClick={() => navigate('/timesheet/history')}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Quick actions: pay-cycle self-service + timesheet submission */}
        <div className="mb-6 flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <button
            onClick={() => navigate('/timesheet/my-timesheet')}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <CalendarDays className="h-4 w-4" />
            {t('viewPayCycle')}
          </button>

          {activeTab === 'timesheet' && (
            <button
              onClick={() => setShowSubmitForm(!showSubmitForm)}
              className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                showSubmitForm
                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              <ClipboardCheck className="h-4 w-4" />
              {showSubmitForm ? t('cancelSubmission') : t('submitForApprovalButton')}
            </button>
          )}
        </div>

        {activeTab === 'timesheet' && submissionError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {submissionError}
          </div>
        )}

        {activeTab === 'timesheet' && submissionSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {submissionSuccess}
          </div>
        )}

        {/* Submission Form */}
        {activeTab === 'timesheet' && showSubmitForm && (
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">{t('submitFormTitle')}</h2>
            <form onSubmit={handleTimesheetSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('startDate')}
                  </label>
                  <Input
                    type="date"
                    value={submissionStartDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubmissionStartDate(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('endDate')}
                  </label>
                  <Input
                    type="date"
                    value={submissionEndDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubmissionEndDate(e.target.value)}
                    min={submissionStartDate}
                    required
                  />
                </div>
              </div>

              {submissionStartDate && submissionEndDate && submissionEndDate >= submissionStartDate && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  {submissionPreviewLoading ? (
                    <span className="text-blue-600">{t('calculatingScheduled')}</span>
                  ) : submissionPreview ? (
                    <>
                      <span className="font-semibold">
                        {t('scheduledWorkingDays', { count: submissionPreview.scheduled_working_days })}
                      </span>
                      <span className="mx-2 text-blue-300">·</span>
                      <span className="font-semibold">{t('hoursShort', { hours: submissionPreview.scheduled_hours.toFixed(2) })}</span>
                      <span className="text-blue-700">{t('rosteredForPeriod')}</span>
                    </>
                  ) : (
                    <span className="text-blue-600">{t('noRosterData')}</span>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notesOptional')}
                </label>
                <textarea
                  value={submissionNotes}
                  onChange={(e) => setSubmissionNotes(e.target.value)}
                  placeholder={t('submissionNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div className="flex space-x-4">
                <Button type="submit" disabled={submissionLoading}>
                  {submissionLoading ? t('submitting') : t('submitForApproval')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowSubmitForm(false);
                    resetTimesheetForm();
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('timesheet')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'timesheet'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('tabTimesheet')}
            </button>
            <button
              onClick={() => setActiveTab('leave')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leave'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('tabLeave')}
            </button>
          </nav>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Timesheet Tab */}
        {activeTab === 'timesheet' && (
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Clock In/Out Section */}
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">{t('timeClock')}</h2>
                
                <div className="space-y-4">
                  {/* Current Status */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('statusLabel')}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isClocked 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isClocked ? t('clockedIn') : t('clockedOut')}
                      </span>
                    </div>
                    
                    {status?.activeSessions && status.activeSessions.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t('clockInLabel')}</span>
                        <span className="font-medium">{formatTime(status.activeSessions[0].clock_in_time)}</span>
                      </div>

                      {status.activeSessions[0].clock_out_time && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('clockOutLabel')}</span>
                          <span className="font-medium">{formatTime(status.activeSessions[0].clock_out_time)}</span>
                        </div>
                      )}

                      {status.activeSessions[0].total_hours && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('totalHoursLabel')}</span>
                          <span className="font-medium">{formatDuration(status.activeSessions[0].total_hours)}</span>
                        </div>
                      )}

                      {currentWorkingTime && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('workingTimeLabel')}</span>
                          <span className="font-medium text-blue-600">{currentWorkingTime}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes Input */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('notesOptional')}
                  </label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
                    placeholder={t('sessionNotesPlaceholder')}
                    className="w-full"
                  />
                </div>

                {/* Location Status */}
                <div className="flex items-center gap-2 text-sm">
                  {locationLoading ? (
                    <>
                      <MapPin className="h-4 w-4 text-blue-400 animate-pulse" />
                      <span className="text-gray-500">{t('fetchingLocation')}</span>
                    </>
                  ) : currentLocation ? (
                    <>
                      <MapPin className="h-4 w-4 text-green-500" />
                      <span className="text-gray-500">
                        {t('locationReady', { accuracy: Math.round(currentLocation.coords.accuracy) })}
                      </span>
                    </>
                  ) : locationError ? (
                    <>
                      <MapPinOff className="h-4 w-4 text-amber-500" />
                      <span className="text-amber-600">{isClocked ? t('locationUnavailableOut') : t('locationUnavailableIn')}</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-400">{isClocked ? t('locationOnClockOut') : t('locationOnClockIn')}</span>
                    </>
                  )}
                </div>

                {lunchNotice && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">{lunchNotice}</p>
                )}

                {/* No-lunch-break option — shown when session > 4 h and about to clock out */}
                {lunchEligible && (
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={skipLunchDeduction}
                        onChange={e => {
                          if (e.target.checked && !tilInForce) { setLunchNeedsTil(true); return; }
                          setLunchNeedsTil(false);
                          setSkipLunchDeduction(e.target.checked);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {t('noLunch')}
                    </label>
                    {skipLunchDeduction && (
                      <p className="mt-1 ml-6 text-xs text-gray-500">
                        {t('noLunchHelp')}
                      </p>
                    )}
                    {lunchNeedsTil && (
                      <div className="mt-2 ml-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-2">
                        <p>
                          <Trans t={t} i18nKey="lunchNeedsTil" components={{ strong: <strong /> }} />
                        </p>
                        {til?.open?.status === 'pending' ? (
                          <p>{t('lunchTilPending')}</p>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate('/timesheet/my-timesheet?til=agree&from=lunch')}
                            className="font-medium text-primary-700 underline"
                          >
                            {t('readTilAgreement')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Clock In/Out Button */}
                <Button
                  onClick={isClocked ? handleClockOut : handleClockIn}
                  disabled={loading}
                  className={`w-full py-3 ${
                    isClocked
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {retrying
                    ? t('stillTrying')
                    : loading
                      ? t('processing')
                      : isClocked ? t('clockOut') : t('clockIn')}
                </Button>
              </div>
            </Card>

            {/* Pay Cycle Summary */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-1">{t('payCycleSummary')}</h2>
              {summary?.period_start && summary?.period_end && (
                <div className="text-sm text-gray-500 mb-4">
                  {formatLocalDateInTz(summary.period_start, orgTimezone)} – {formatLocalDateInTz(summary.period_end, orgTimezone)}
                </div>
              )}

              {!summary ? (
                <div className="text-gray-500 text-sm">{t('loadingSummary')}</div>
              ) : summary.total_sessions === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <Clock className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm font-medium text-gray-500">{t('noHours')}</p>
                  <p className="text-xs mt-1">{t('noHoursHelp')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Row 1: Hours Worked + Scheduled Hours */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatDuration(summary.total_hours)}
                      </div>
                      <div className="text-sm text-blue-600">{t('hoursWorked')}</div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {formatDuration(summary.scheduled_hours)}
                      </div>
                      <div className="text-sm text-green-600">{t('scheduledHours')}</div>
                    </div>
                  </div>

                  {/* Row 2: Days Worked */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-lg font-semibold text-purple-600">
                      {summary.total_days_worked}
                    </div>
                    <div className="text-sm text-purple-600">{t('daysWorked')}</div>
                  </div>

                  {/* Row 3: Overtime (weekly) or Time in Lieu Accrued (fortnightly) */}
                  {(() => {
                    const summaryIsFortnightly = summary.pay_frequency === 'fortnightly';
                    const threshold = summary.overtime_threshold ?? (summaryIsFortnightly ? 80 : 38);
                    return (
                      <div className={`p-4 rounded-lg ${summary.overtime_hours > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className={`text-sm font-medium ${summary.overtime_hours > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                            {summaryIsFortnightly ? t('tilAccrued') : t('overtime')}
                          </div>
                          <div className={`text-lg font-bold ${summary.overtime_hours > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {summary.overtime_hours > 0 ? formatDuration(summary.overtime_hours) : '—'}
                          </div>
                        </div>
                        <div className={`text-xs mt-1 ${summary.overtime_hours > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {summaryIsFortnightly
                            ? summary.overtime_threshold_basis === 'roster'
                              ? (summary.overtime_hours > 0 ? t('thresholdRosterAccruing', { threshold }) : t('thresholdRoster', { threshold }))
                              : (summary.overtime_hours > 0 ? t('thresholdFortnightAccruing', { threshold }) : t('thresholdFortnight', { threshold }))
                            : (summary.overtime_hours > 0 ? t('thresholdWeekOvertime', { threshold }) : t('thresholdWeek', { threshold }))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Row 4: Public Holidays in this pay cycle */}
                  {summary.public_holiday_days > 0 && (
                    <div className="p-4 rounded-lg bg-rose-50">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-rose-700">{t('publicHolidays')}</div>
                        <div className="text-lg font-bold text-rose-600">
                          {formatDuration(summary.public_holiday_hours)}
                        </div>
                      </div>
                      {summary.public_holiday_worked_hours > 0 && (
                        <div className="flex items-center justify-between mt-1">
                          <div className="text-xs text-rose-600">{t('workedOnHoliday')}</div>
                          <div className="text-sm font-semibold text-rose-600">
                            +{formatDuration(summary.public_holiday_worked_hours)}
                          </div>
                        </div>
                      )}
                      <div className="text-xs mt-2 text-rose-600 space-y-0.5">
                        {summary.public_holidays.map((ph, i) => (
                          <div key={i}>{ph.name}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
            </div>

            {/* Leave Entitlements */}
            {leaveBalances.length > 0 && (
              <Card className="p-5 mt-8">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('leaveEntitlements')}</h3>
                <div className="space-y-2">
                  {leaveBalances.map(b => (
                    <div key={b.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {balanceLabel(b.leave_type)}
                      </span>
                      <div className="flex items-center gap-4 text-gray-500">
                        <span>{t('accrued', { hours: Number(b.total_hours).toFixed(1) })}</span>
                        <span>{t('used', { hours: Number(b.used_hours).toFixed(1) })}</span>
                        <span className="font-medium text-gray-800">{t('available', { hours: Number(b.available_hours).toFixed(1) })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Company Policy Summary */}
            {user?.organisation_id && (
              <PolicySummaryCard orgId={user.organisation_id} userId={user.id} payFrequency={user.pay_frequency ?? undefined} />
            )}

            {/* Submissions: unsubmitted periods + submission history */}
            <div className="mt-8">
              {/* Unsubmitted Pay Periods */}
              {unsubmittedPeriods.length > 0 && (
                <Card className="p-6 mb-6 border-amber-300 bg-amber-50">
                  <h2 className="text-xl font-semibold mb-4 text-amber-800">{t('awaitingSubmission')}</h2>
                  <div className="space-y-3">
                    {unsubmittedPeriods.map(s => {
                      // Period is guaranteed fully elapsed by isUnsubmitted's
                      // period_end <= today guard, so we submit the whole range.
                      const effectiveEnd = s.period_end;
                      const rows = categoryBreakdown.get(s.period_start) ?? [];
                      // Group rows by date, preserving date order
                      const byDate = rows.reduce<Map<string, DailyCategoryRow[]>>((acc, r) => {
                        const list = acc.get(r.date) ?? [];
                        list.push(r);
                        acc.set(r.date, list);
                        return acc;
                      }, new Map());
                      // Merge scheduled days with worked days so missed days are visible.
                      const scheduledDays = scheduledByPeriod.get(s.period_start) ?? [];
                      const scheduledByDate = new Map(scheduledDays.map(d => [d.date, d]));
                      const allDates = new Set<string>([
                        ...byDate.keys(),
                        ...scheduledDays.map(d => d.date)
                      ]);
                      const sortedDates = Array.from(allDates).sort();
                      // A rostered public holiday with nothing recorded is a day off, not a missed day.
                      const missedDays = scheduledDays.filter(d => !byDate.has(d.date) && !d.public_holiday);
                      return (
                        <div key={s.period_start} className="border border-amber-200 rounded-lg p-4 bg-white">
                          {/* Period header + submit */}
                          <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
                            <div>
                              <h3 className="font-semibold">
                                {formatLocalDateInTz(s.period_start, orgTimezone)} – {formatLocalDateInTz(effectiveEnd, orgTimezone)}
                              </h3>
                              <div className="text-sm text-gray-600 mt-0.5">
                                {t('periodTotal', { duration: formatDuration(s.total_hours), count: s.total_sessions })}
                                {scheduledDays.length > 0 && (
                                  <>
                                    <span className="mx-1.5 text-gray-300">·</span>
                                    <span>
                                      {t('rostered')}{' '}
                                      <span className="font-medium text-gray-700">
                                        {t('rosteredDays', { count: scheduledDays.length, hours: scheduledDays.reduce((sum, d) => sum + d.scheduled_hours, 0).toFixed(2) })}
                                      </span>
                                    </span>
                                  </>
                                )}
                                {missedDays.length > 0 && (
                                  <>
                                    <span className="mx-1.5 text-gray-300">·</span>
                                    <span className="font-medium text-red-600">
                                      {t('missedDays', { count: missedDays.length })}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={() => handleQuickSubmit(s.period_start, effectiveEnd)}
                              disabled={quickSubmittingPeriod !== null}
                            >
                              {quickSubmittingPeriod === s.period_start ? t('submittingEllipsis') : t('submitForApproval')}
                            </Button>
                          </div>
                          {/* Daily summary — includes scheduled days that have no entries */}
                          {sortedDates.length > 0 && (
                            <div className="border-t border-amber-100 pt-3 space-y-1.5">
                              {sortedDates.map(date => {
                                const cats = byDate.get(date);
                                const scheduled = scheduledByDate.get(date);
                                const worked = cats ? cats.reduce((sum, r) => sum + toNumOr(r.hours), 0) : 0;
                                const holiday = !cats ? scheduled?.public_holiday : undefined;
                                const isMissed = !cats && !!scheduled && !holiday;
                                return (
                                  <div key={date} className={`flex items-center gap-3 text-sm ${isMissed ? 'text-red-700' : ''}`}>
                                    <span className={`w-24 shrink-0 ${isMissed ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {formatLocalDateInTz(date, orgTimezone, { weekday: 'short', day: 'numeric', month: 'short' })}
                                    </span>
                                    <span className={`w-16 shrink-0 font-medium ${isMissed ? 'text-red-600' : 'text-gray-700'}`}>
                                      {cats ? formatDuration(worked) : '—'}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-1">
                                      {cats
                                        ? cats.map(r => (
                                            <CategoryBadge key={r.category} category={r.category} hours={toNumOr(r.hours)} />
                                          ))
                                        : holiday ? (
                                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                            {t('publicHolidayDay', { name: holiday })}
                                          </span>
                                        ) : (
                                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                            {t('missedDay', { hours: scheduled!.scheduled_hours.toFixed(2), start: scheduled!.start_time, end: scheduled!.end_time })}
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Submissions List */}
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">{t('yourSubmissions')}</h2>

                {timesheetSubmissions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {t('noSubmissions')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timesheetSubmissions.map((submission) => (
                      <div 
                        key={submission.id} 
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold">
                                {t('submissionTitle', { start: formatDateInTz(submission.start_date, orgTimezone), end: formatDateInTz(submission.end_date, orgTimezone) })}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(submission.status)}`}>
                                {t(`status.${submission.status}`, { defaultValue: submission.status })}
                              </span>
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>
                                <span className="font-medium">{t('totalHoursLabel')}</span> {t('hoursValue', { hours: parseFloat(submission.total_hours as any).toFixed(2) })}
                              </div>
                              <div>
                                <span className="font-medium">{t('entriesLabel')}</span> {t('entriesValue', { n: submission.timesheet_entries_count })}
                              </div>
                              {submission.notes && (
                                <div>
                                  <span className="font-medium">{t('notesLabel')}</span> {submission.notes}
                                </div>
                              )}
                              {submission.rejection_reason && (
                                <div className="text-red-600">
                                  <span className="font-medium">{t('rejectionReasonLabel')}</span> {submission.rejection_reason}
                                </div>
                              )}
                              {submission.approved_by_username && (
                                <div>
                                  <span className="font-medium">{t('reviewedByLabel')}</span> {submission.approved_by_username}
                                </div>
                              )}
                            </div>
                            {submission.status === 'pending' && (
                              <div className="mt-3">
                                <Button
                                  variant="secondary"
                                  onClick={() => handleRecallSubmission(submission.id)}
                                >
                                  {t('recallSubmission')}
                                </Button>
                              </div>
                            )}
                            {submission.status === 'rejected' && (
                              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                                {t('submissionRejected')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* Leave Tab */}
        {activeTab === 'leave' && (
          <>
            {leaveError && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {leaveError}
              </div>
            )}

            {leaveSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {leaveSuccess}
              </div>
            )}

            {/* New Request Button */}
            <div className="mb-6">
              <Button
                onClick={() => setShowLeaveForm(!showLeaveForm)}
                variant={showLeaveForm ? "secondary" : "primary"}
              >
                {showLeaveForm ? t('cancel') : t('newLeaveRequestButton')}
              </Button>
            </div>

            {/* New Request Form */}
            {showLeaveForm && (
              <Card className="p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">{t('newLeaveRequest')}</h2>
                <form onSubmit={handleLeaveSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('leaveTypeLabel')}
                    </label>
                    <select
                      value={leaveType}
                      onChange={(e) => setLeaveType(e.target.value as LeaveType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="annual">
                        {withAvailable(t('leaveOption.annual'), availableFor('annual'))}
                      </option>
                      <option value="personal">
                        {withAvailable(t('leaveOption.personal'), availableFor('personal'))}
                      </option>
                      <option value="long_service">
                        {withAvailable(t('leaveOption.long_service'), availableFor('long_service'))}
                      </option>
                      <option value="unpaid">{t('leaveOption.unpaid')}</option>
                      <option value="other">{t('leaveOption.other')}</option>
                      {isFortnightly && (
                        <option value="time_in_lieu">
                          {withAvailable(t('leaveOption.time_in_lieu'), tilBalance)}
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isHourlyLeave}
                        onChange={(e) => {
                          setIsHourlyLeave(e.target.checked);
                          if (!e.target.checked) {
                            setLeaveStartTime('');
                            setLeaveEndTime('');
                          }
                        }}
                        className="w-4 h-4 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('hourlyLeave')}</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('startDate')}
                      </label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isHourlyLeave ? t('date') : t('endDate')}
                      </label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                        min={startDate}
                        required
                      />
                    </div>
                  </div>

                  {/* TIL balance info banner */}
                  {leaveType === 'time_in_lieu' && tilBalance !== null && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-md px-4 py-3 text-sm text-indigo-800">
                      <Trans t={t} i18nKey="tilBalance" values={{ hours: tilBalance.toFixed(1) }} components={{ strong: <strong /> }} />
                    </div>
                  )}

                  {/* Scheduled hours preview for full-day leave */}
                  {!isHourlyLeave && startDate && endDate && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm">
                      {leaveHoursPreviewLoading ? (
                        <span className="text-blue-600">{t('calculatingScheduled')}</span>
                      ) : leaveHoursPreview ? (
                        <span className="text-blue-800">
                          <Trans
                            t={t}
                            i18nKey="workingDaysPreview"
                            count={leaveHoursPreview.working_days}
                            values={{ count: leaveHoursPreview.working_days, hours: leaveHoursPreview.scheduled_hours.toFixed(2) }}
                            components={{ strong: <strong /> }}
                          />
                        </span>
                      ) : null}
                    </div>
                  )}

                  {/* Projected balance preview */}
                  {startDate && BALANCE_TRACK_TYPES.includes(leaveType) && (
                    <div className={`border rounded-md px-4 py-3 text-sm ${
                      balancePreview && balancePreview.projectionApplied && leaveHoursPreview &&
                      balancePreview.currentBalance < leaveHoursPreview.scheduled_hours &&
                      balancePreview.projectedBalance >= leaveHoursPreview.scheduled_hours
                        ? 'bg-amber-50 border-amber-200'
                        : balancePreview && leaveHoursPreview &&
                          balancePreview.projectedBalance < leaveHoursPreview.scheduled_hours
                        ? 'bg-red-50 border-red-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      {balancePreviewLoading ? (
                        <span className="text-gray-500">{t('checkingBalance')}</span>
                      ) : balancePreview ? (
                        <div className="space-y-1">
                          <div className="text-gray-700">
                            <Trans t={t} i18nKey="currentBalance" values={{ hours: balancePreview.currentBalance.toFixed(2) }} components={{ strong: <strong /> }} />
                          </div>
                          {balancePreview.projectionApplied && (
                            <div className="text-gray-700">
                              <Trans t={t} i18nKey="estBalance" values={{ hours: balancePreview.projectedBalance.toFixed(2) }} components={{ strong: <strong /> }} />
                              <span className="text-gray-500 ml-1">
                                {t('estAccrual', { accrued: balancePreview.projectedAccrual.toFixed(2), work: balancePreview.projectedWorkHours.toFixed(0) })}
                              </span>
                            </div>
                          )}
                          {balancePreview.projectionApplied && leaveHoursPreview &&
                           balancePreview.currentBalance < leaveHoursPreview.scheduled_hours &&
                           balancePreview.projectedBalance >= leaveHoursPreview.scheduled_hours && (
                            <div className="text-amber-700 text-xs mt-1">
                              {t('projectedCovers')}
                            </div>
                          )}
                          {leaveHoursPreview &&
                           balancePreview.projectedBalance < leaveHoursPreview.scheduled_hours && (
                            <div className="text-red-700 text-xs mt-1">
                              {t('belowZero', { hours: (leaveHoursPreview.scheduled_hours - balancePreview.projectedBalance).toFixed(2), max: MAX_NEGATIVE_LEAVE_HOURS })}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {isHourlyLeave && (
                    <div className="space-y-2">
                      {/* Quick-fill. A short day can be short at either end —
                          late in, early out, or both — so the server returns
                          every scheduled block not already covered by work and
                          each one gets its own button. Guessing "from the last
                          clock-out to the scheduled finish" produced an empty
                          window for a late arrival who then worked through. */}
                      {leaveDayInfo && leaveDayInfo.is_working_day && (
                        <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs space-y-2">
                          <span className="block text-gray-700">
                            <Trans
                              t={t}
                              i18nKey="scheduledDay"
                              values={{ start: leaveDayInfo.scheduled_start_time, end: leaveDayInfo.scheduled_end_time, hours: leaveDayInfo.scheduled_hours.toFixed(2) }}
                              components={{ strong: <strong /> }}
                            />
                            {leaveDayInfo.worked_hours > 0 && (
                              <>{' '}<Trans t={t} i18nKey="workedDay" values={{ hours: leaveDayInfo.worked_hours.toFixed(2) }} components={{ strong: <strong /> }} />
                                {leaveDayInfo.worked_segments.length > 0 && (
                                  <> ({leaveDayInfo.worked_segments.map(w => `${w.start_time}–${w.end_time}`).join(', ')})</>
                                )}
                              </>
                            )}
                          </span>
                          {(() => {
                            // Zero-hour blocks are the unpaid break, not a shortfall.
                            const gaps = leaveDayInfo.uncovered_segments.filter(g => g.hours > 0);
                            if (gaps.length === 0) {
                              return (
                                <span className="block text-gray-500">
                                  {t('allCovered')}
                                </span>
                              );
                            }
                            return (
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-gray-700">
                                  {gaps.length === 1 ? t('hoursYouMissed') : t('awayInBlocks', { n: gaps.length })}
                                </span>
                                {gaps.map(gap => (
                                  <button
                                    key={`${gap.start_time}-${gap.end_time}`}
                                    type="button"
                                    onClick={() => {
                                      setLeaveStartTime(gap.start_time);
                                      setLeaveEndTime(gap.end_time);
                                    }}
                                    className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium"
                                  >
                                    {t('fillGap', { start: gap.start_time, end: gap.end_time, hours: gap.hours.toFixed(2) })}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                          {leaveDayInfo.uncovered_segments.filter(g => g.hours > 0).length > 1 && (
                            <span className="block text-gray-500">
                              {t('separateRequests')}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('leaveStartsAt')}
                          </label>
                          <Input
                            type="time"
                            value={leaveStartTime}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const picked = e.target.value;
                              setLeaveStartTime(picked);
                              // Convenience: with no end set yet, run to the end of the
                              // unworked block the start falls in — not blindly to the
                              // scheduled finish, which would span hours already worked.
                              if (!picked || leaveEndTime) return;
                              const gap = leaveDayInfo?.uncovered_segments.find(
                                g => g.hours > 0 && picked >= g.start_time && picked < g.end_time
                              );
                              const fallback = leaveDayInfo?.scheduled_end_time;
                              if (gap) setLeaveEndTime(gap.end_time);
                              else if (fallback && picked < fallback) setLeaveEndTime(fallback);
                            }}
                            required={isHourlyLeave}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('leaveEndsAt')}
                          </label>
                          <Input
                            type="time"
                            value={leaveEndTime}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeaveEndTime(e.target.value)}
                            min={leaveStartTime || undefined}
                            required={isHourlyLeave}
                          />
                        </div>
                      </div>
                      {leaveHours ? (
                        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                          <Trans
                            t={t}
                            i18nKey="requesting"
                            values={{ hours: leaveHours, date: startDate || t('theSelectedDate') }}
                            components={{ strong: <strong /> }}
                          />
                          {leaveDayInfo?.is_working_day
                            && Math.abs(parseFloat(leaveHours) - (timeToMinutes(leaveEndTime) - timeToMinutes(leaveStartTime)) / 60) > 0.01 && (
                            <>{' '}{t('lessBreak', { start: leaveStartTime, end: leaveEndTime })}</>
                          )}
                          {' '}{t('trimmed')}
                        </p>
                      ) : (leaveStartTime && leaveEndTime) ? (
                        <p className="text-xs text-red-600">{t('endAfterStart')}</p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {leaveDayInfo?.is_working_day && leaveDayInfo.scheduled_start_time
                            ? t('pickHoursWithin', { start: leaveDayInfo.scheduled_start_time, end: leaveDayInfo.scheduled_end_time })
                            : t('pickHours')}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reasonOptional')}
                    </label>
                    <textarea
                      value={leaveReason}
                      onChange={(e) => setLeaveReason(e.target.value)}
                      placeholder={t('reasonPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      maxLength={500}
                    />
                  </div>

                  {isSickLeave && (
                    <div className="space-y-3">
                      {certBlocksApproval && certCheck && (
                        <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            {certCheck.reasons.map((r, i) => <p key={i}>{r}</p>)}
                            <p className="font-medium">{t('certMustAttach')}</p>
                          </div>
                        </div>
                      )}
                      {certWarnOnly && certCheck && (
                        <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            {certCheck.reasons.map((r, i) => <p key={i}>{r}</p>)}
                          </div>
                        </div>
                      )}

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasCertificate}
                          disabled={certBlocksApproval}
                          onChange={(e) => {
                            setHasCertificate(e.target.checked);
                            if (!e.target.checked) {
                              setCertificateFile(null);
                              if (certFileInputRef.current) certFileInputRef.current.value = '';
                              if (certCameraInputRef.current) certCameraInputRef.current.value = '';
                            }
                          }}
                          className="w-4 h-4 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {t('haveCertificate')}
                          {certBlocksApproval && <span className="text-red-600 ml-1">*</span>}
                        </span>
                      </label>

                      {hasCertificate && (
                        <div>
                          {certificateFile ? (
                            <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <Paperclip className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="text-sm text-blue-800 truncate flex-1">{certificateFile.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setCertificateFile(null);
                                  if (certFileInputRef.current) certFileInputRef.current.value = '';
                                  if (certCameraInputRef.current) certCameraInputRef.current.value = '';
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {/* Hidden inputs */}
                              <input
                                ref={certFileInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  if (file && file.size > 10 * 1024 * 1024) {
                                    setLeaveError(t('errors.certTooBig'));
                                    e.target.value = '';
                                    return;
                                  }
                                  setCertificateFile(file);
                                }}
                              />
                              <input
                                ref={certCameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  if (file && file.size > 10 * 1024 * 1024) {
                                    setLeaveError(t('errors.photoTooBig'));
                                    e.target.value = '';
                                    return;
                                  }
                                  setCertificateFile(file);
                                }}
                              />
                              {/* Visible buttons */}
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => certFileInputRef.current?.click()}
                                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  <Paperclip className="h-4 w-4" />
                                  {t('chooseFile')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => certCameraInputRef.current?.click()}
                                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  <Camera className="h-4 w-4" />
                                  {t('takePhoto')}
                                </button>
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{t('fileHint')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex space-x-4">
                    <Button type="submit" disabled={leaveLoading}>
                      {leaveLoading ? t('submitting') : t('submitRequest')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowLeaveForm(false);
                        resetLeaveForm();
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {/* Leave Requests List */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">{t('yourLeaveRequests')}</h2>

              {leaveRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t('noLeaveRequests')}
                </div>
              ) : (
                <div className="space-y-4">
                  {leaveRequests.map((request) => (
                    <div 
                      key={request.id} 
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-semibold">
                              {leaveRequestTitle(request.leave_type)}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(request.status)}`}>
                              {t(`status.${request.status}`, { defaultValue: request.status })}
                            </span>
                          </div>
                          
                          <div className="text-sm text-gray-600 space-y-1">
                            <div>
                              <span className="font-medium">{t('datesLabel')}</span>{' '}
                              {formatDateInTz(request.start_date, orgTimezone)} -{' '}
                              {formatDateInTz(request.end_date, orgTimezone)}
                            </div>
                            <div>
                              <span className="font-medium">{t('durationLabel')}</span> {request.hours ? t('hoursValue', { hours: request.hours }) : t('daysValue', { n: request.total_days })}
                              {!request.hours && request.scheduled_hours != null && (
                                <span className="ml-2 text-gray-500">{t('scheduledHoursValue', { hours: parseFloat(String(request.scheduled_hours)).toFixed(2) })}</span>
                              )}
                            </div>
                            {request.reason && (
                              <div>
                                <span className="font-medium">{t('reasonLabel')}</span> {request.reason}
                              </div>
                            )}
                            {request.rejection_reason && (
                              <div className="text-red-600">
                                <span className="font-medium">{t('rejectionReasonLabel')}</span> {request.rejection_reason}
                              </div>
                            )}
                            {request.approved_by_username && (
                              <div>
                                <span className="font-medium">{t('reviewedByLabel')}</span> {request.approved_by_username}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {['pending', 'approved'].includes(request.status) && (
                          <Button
                            variant="secondary"
                            onClick={() => handleCancelLeave(request.id)}
                            className="ml-4"
                          >
                            {t('cancel')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
        </div>
    </div>
  );
};

export default Timesheet;
